import React, { FC, useContext, useMemo } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import { PublishDealRoomValues, REVENUE_DESCRIPTION } from 'models/DealRoom';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import PluscircleIcon from 'icons/Pluscircle.icon';
import TrashIcon from 'icons/Trash.icon';
import InputField from 'components/InputField';
import { Field, useFormState } from 'react-final-form';
import validations from 'validations';
import SelectField from 'components/SelectField';
import useMatchMedia from 'hooks/useMatchMedia';
import { PERCENTRAGE } from 'data/global';
import {
  addPercentageSuffix,
  extractValueFromPercentage,
  shouldShowFrachisorFee,
} from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';

import './RevenueSharing.styles.scss';
import './RevenueSharing.styles.responsive.scss';

const INITIAL_STATE = {};

const ARRAY_HEADER = ['order', 'description', 'percentage'];
const MOBILE_ARRAY_HEADER = ['#', 'description', '%'];

type RevenueSharingProps = {
  className?: string;
};

const RevenueSharing: FC<RevenueSharingProps> = ({ className }) => {
  const { hasEnterprise } = useContext(CurrentUserContext);

  const showFrachisorFee = shouldShowFrachisorFee(hasEnterprise);

  const classes = classNames(
    'revenue-sharing',
    'white-panel',
    'fields-array',
    className,
  );
  const {
    values: { colleagues, revenueShares, franchisorFeePercentage },
  } = useFormState<Partial<PublishDealRoomValues>>();

  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const createSelectOptions = useCreateSelectOptions();

  const brokerOptions = useMemo(
    () => [
      { label: t('none'), value: null },
      ...colleagues.map(({ email, name }) => ({
        label: name || email,
        value: email,
        disabled: revenueShares.some((share) => share.email === email),
      })),
    ],
    [colleagues, revenueShares, t],
  );

  const revenueDescriptionOptions = useMemo(
    () => createSelectOptions(Object.values(REVENUE_DESCRIPTION)),
    [createSelectOptions],
  );

  return (
    <div className={classes}>
      <div>
        {Object.values(isPhablet ? MOBILE_ARRAY_HEADER : ARRAY_HEADER).map(
          (el) => (
            <span key={el}>{t(el)}</span>
          ),
        )}
      </div>

      {showFrachisorFee && (
        <div className="revenue-sharing__fields">
          <span className="order"># {1}</span>
          <div className="franchisor">
            <span>{t('franchisor')}</span>
            {!isPhablet && <span>{t('franchisorDescription')}</span>}
          </div>
          <Field
            name="franchisorFeePercentage"
            component={InputField}
            placeholder={PERCENTRAGE}
            format={addPercentageSuffix}
            parse={(value) =>
              extractValueFromPercentage(value, franchisorFeePercentage)
            }
          />
        </div>
      )}

      <FieldArray name="revenueShares">
        {({ fields }) => (
          <>
            {fields.map((name, index) => {
              const orderNum = showFrachisorFee ? index + 2 : index + 1;
              const field = fields?.value[index];

              const percentage = field?.percentage;

              return (
                <div key={name} className="revenue-sharing__fields">
                  <span className="order"># {orderNum}</span>

                  <div className="description">
                    <Field
                      renderAsPortal
                      name={`${name}.description`}
                      portalClassName="deal-select-portal"
                      placeholder={t('description')}
                      options={revenueDescriptionOptions}
                      component={SelectField}
                      customOnChange={(description: string) =>
                        fields.update(index, { description, percentage })
                      }
                    />
                    {field?.description === REVENUE_DESCRIPTION.ADD_CUSTOM ? (
                      <Field
                        name={`${name}.label`}
                        placeholder={t('addCustomLabel')}
                        component={InputField}
                        validate={validations.required()}
                      />
                    ) : (
                      field?.description ===
                        REVENUE_DESCRIPTION.BROKERS_COMMISSION && (
                        <Field
                          renderAsPortal
                          name={`${name}.email`}
                          portalClassName="deal-select-portal"
                          placeholder={t('brokerOptional')}
                          options={brokerOptions}
                          component={SelectField}
                        />
                      )
                    )}
                  </div>

                  <Field
                    name={`${name}.percentage`}
                    component={InputField}
                    placeholder={PERCENTRAGE}
                    format={addPercentageSuffix}
                    parse={(value) =>
                      extractValueFromPercentage(value, percentage)
                    }
                  />

                  <Button
                    variant="icon"
                    icon={TrashIcon}
                    onClick={() => fields.remove(index)}
                  />
                </div>
              );
            })}

            <Button
              variant="link"
              iconPosition="right"
              icon={PluscircleIcon}
              onClick={() => fields.push(INITIAL_STATE)}
            >
              {t('addAnotherRevenueShare')}
            </Button>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default RevenueSharing;
